/**
 * Google broker login via Kong → auth-service.
 */

import { kongBrowserFetchUrl } from "@/lib/kongApi";
import { fetchWithTimeout, LOGIN_FETCH_TIMEOUT_MS } from "@/lib/fetchWithTimeout";

async function parseErrorMessage(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    if (typeof d.message === "string") return d.message;
    if (typeof d.error === "string") return d.error;
  }
  if (typeof data?.message === "string") return data.message;
  return res.statusText || "Request failed";
}

const ERROR_LABELS = {
  USER_NOT_REGISTERED: "No account found for this email. Ask your administrator for access.",
  EMAIL_MISMATCH: "Google account email does not match the email you entered.",
  GOOGLE_SESSION_EXPIRED: "Google sign-in timed out. Please try again.",
  GOOGLE_AUTH_FAILED: "Google sign-in failed. Please try again or use password login.",
  REDIS_UNAVAILABLE: "Sign-in service is temporarily unavailable.",
  GOOGLE_AUTH_NOT_CONFIGURED: "Google sign-in is not configured on this environment.",
  totp_challenge_unavailable: "Two-factor verification is temporarily unavailable. Try again shortly.",
  totp_store_unavailable: "Two-factor verification is temporarily unavailable. Try again shortly.",
};

export function friendlyGoogleError(code) {
  return ERROR_LABELS[code] || ERROR_LABELS.GOOGLE_AUTH_FAILED;
}

/** Dedupe parallel complete calls (React StrictMode mounts effects twice in dev). */
const completeLoginInflight = new Map();

class GoogleAuthApiClient {
  /**
   * @param {string | undefined} email Optional hint for faster tenant realm lookup after Google returns.
   * @returns {Promise<{ redirect_url: string }>}
   */
  async startLogin(email) {
    const trimmed = String(email || "").trim();
    const url = kongBrowserFetchUrl("/api/v1/auth/google/start");
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(trimmed ? { email: trimmed } : {}),
      },
      LOGIN_FETCH_TIMEOUT_MS
    );
    if (!res.ok) {
      const msg = await parseErrorMessage(res);
      throw new Error(msg);
    }
    return res.json();
  }

  /**
   * @param {string} state
   * @returns {Promise<import("@/api/authApi").LoginResult>}
   */
  async completeLogin(state) {
    const st = String(state || "").trim();
    if (!st) {
      throw new Error("Missing sign-in state. Please start Google login again.");
    }
    const existing = completeLoginInflight.get(st);
    if (existing) {
      return existing;
    }
    const promise = this._fetchCompleteLogin(st).catch((err) => {
      completeLoginInflight.delete(st);
      throw err;
    });
    completeLoginInflight.set(st, promise);
    return promise;
  }

  async _fetchCompleteLogin(st) {
    const url = kongBrowserFetchUrl("/api/v1/auth/google/complete");
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ state: st }),
      },
      LOGIN_FETCH_TIMEOUT_MS
    );
    if (!res.ok) {
      const msg = await parseErrorMessage(res);
      throw new Error(msg);
    }
    const body = await res.json();
    if (String(body.status || "") === "totp_required" && body.totp_session) {
      return {
        status: "totp_required",
        totp_session: String(body.totp_session),
      };
    }
    return {
      status: "ok",
      token: body.token || body.access_token,
      email: body.user?.email || "",
      tenant_id: body.tenant_id,
      realm: body.realm,
      roles: body.roles || [],
      workspace_client_allowlist: body.workspace_client_allowlist || [],
      default_workspace_id: body.default_workspace_id ?? "",
    };
  }
}

export const googleAuthApi = new GoogleAuthApiClient();
